import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Users, UserPlus, Shield, Edit2, Trash2, Eye, EyeOff, Key, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement({ token, currentUser }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    permissions: {
      customers: true,
      suppliers: true,
      delivery_notes: true,
      orders: true,
      tours: true,
      settings: false
    },
    can_create_users: false
  });

  const [editUserData, setEditUserData] = useState({
    email: '',
    role: 'user',
    permissions: {},
    can_create_users: false,
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/users`, newUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Benutzer erfolgreich erstellt!');
      setCreateDialogOpen(false);
      resetNewUserForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/auth/users/${userToEdit.id}`, editUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Benutzerrechte erfolgreich aktualisiert!');
      setEditDialogOpen(false);
      setUserToEdit(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/auth/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Benutzer erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.put(
        `${API}/auth/users/${userToChangePassword.id}/password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Passwort für ${userToChangePassword.username} erfolgreich geändert`);
      setPasswordDialogOpen(false);
      setUserToChangePassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsLoading(false);
    }
  };

  const openPasswordDialog = (user) => {
    setUserToChangePassword(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setUserToEdit(user);
    setEditUserData({
      email: user.email || '',
      role: user.role,
      permissions: user.permissions || {},
      can_create_users: user.can_create_users || false,
      is_active: user.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const resetNewUserForm = () => {
    setNewUserData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      permissions: {
        customers: true,
        suppliers: true,
        delivery_notes: true,
        orders: true,
        tours: true,
        settings: false
      },
      can_create_users: false
    });
    setShowPassword(false);
  };

  const togglePermission = (permission, isEdit = false) => {
    if (isEdit) {
      setEditUserData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permission]: !prev.permissions[permission]
        }
      }));
    } else {
      setNewUserData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permission]: !prev.permissions[permission]
        }
      }));
    }
  };

  const permissionLabels = {
    customers: 'Kunden',
    suppliers: 'Lieferanten',
    delivery_notes: 'Lieferscheine',
    orders: 'Bestellungen',
    tours: 'Tourenplanung',
    settings: 'Einstellungen'
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Benutzerverwaltung
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Benutzer verwalten und Rechte vergeben
          </p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="metaxon-primary text-white"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Benutzer anlegen
        </Button>
      </div>

      {/* Users List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <Card key={user.id} className={`${user.id === currentUser.id ? 'border-2 border-blue-500' : ''}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${user.role === 'admin' ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}>
                    {user.role === 'admin' ? (
                      <Shield className="h-4 w-4 text-white" />
                    ) : (
                      <Users className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-base">{user.username}</CardTitle>
                    {user.id === currentUser.id && (
                      <span className="text-xs text-blue-600 font-medium">Sie</span>
                    )}
                  </div>
                </div>
                {user.id !== currentUser.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUserToDelete(user);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {user.email && (
                <div className="text-sm text-gray-600">{user.email}</div>
              )}
              
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                  user.role === 'admin' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                </span>
                {user.can_create_users && (
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-700">
                    <UserPlus className="h-3 w-3 inline mr-1" />
                    Kann Benutzer anlegen
                  </span>
                )}
              </div>

              {user.is_active === false && (
                <div className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-700">
                  Deaktiviert
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => openEditDialog(user)}
              >
                <Edit2 className="mr-2 h-3 w-3" />
                Rechte bearbeiten
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => openPasswordDialog(user)}
              >
                <Key className="mr-2 h-3 w-3" />
                Passwort ändern
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Benutzer und vergeben Sie Zugriffsrechte
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-username">Benutzername *</Label>
              <Input
                id="new-username"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                required
                placeholder="Benutzername"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-email">E-Mail (optional)</Label>
              <Input
                id="new-email"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="benutzer@firma.de"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">Passwort *</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  required
                  placeholder="Sicheres Passwort wählen"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-role">Rolle</Label>
              <Select value={newUserData.role} onValueChange={(value) => setNewUserData({ ...newUserData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-semibold">Modulzugriff</Label>
              <p className="text-sm text-gray-600">Wählen Sie, auf welche Module der Benutzer zugreifen darf</p>
              <div className="space-y-2">
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor={`perm-${key}`} className="cursor-pointer">
                      {label}
                    </Label>
                    <Checkbox
                      id={`perm-${key}`}
                      checked={newUserData.permissions[key]}
                      onCheckedChange={() => togglePermission(key)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
              <div>
                <Label htmlFor="can-create-users" className="cursor-pointer font-semibold">
                  Darf weitere Benutzer anlegen
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  Benutzer kann selbst neue Benutzer erstellen
                </p>
              </div>
              <Switch
                id="can-create-users"
                checked={newUserData.can_create_users}
                onCheckedChange={(checked) => setNewUserData({ ...newUserData, can_create_users: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetNewUserForm();
              }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isLoading} className="metaxon-primary text-white">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt...</> : 'Benutzer anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Benutzerrechte bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Rechte für: {userToEdit?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUserData.email}
                onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                placeholder="benutzer@firma.de"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Rolle</Label>
              <Select value={editUserData.role} onValueChange={(value) => setEditUserData({ ...editUserData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-semibold">Modulzugriff</Label>
              <div className="space-y-2">
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor={`edit-perm-${key}`} className="cursor-pointer">
                      {label}
                    </Label>
                    <Checkbox
                      id={`edit-perm-${key}`}
                      checked={editUserData.permissions[key] || false}
                      onCheckedChange={() => togglePermission(key, true)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
              <div>
                <Label htmlFor="edit-can-create-users" className="cursor-pointer font-semibold">
                  Darf weitere Benutzer anlegen
                </Label>
              </div>
              <Switch
                id="edit-can-create-users"
                checked={editUserData.can_create_users}
                onCheckedChange={(checked) => setEditUserData({ ...editUserData, can_create_users: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg bg-amber-50">
              <div>
                <Label htmlFor="edit-is-active" className="cursor-pointer font-semibold">
                  Benutzer aktiv
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  Deaktivierte Benutzer können sich nicht anmelden
                </p>
              </div>
              <Switch
                id="edit-is-active"
                checked={editUserData.is_active}
                onCheckedChange={(checked) => setEditUserData({ ...editUserData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setEditDialogOpen(false);
                setUserToEdit(null);
              }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isLoading} className="metaxon-primary text-white">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert...</> : 'Änderungen speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Benutzer "{userToDelete?.username}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Benutzer löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
            <DialogDescription>
              Neues Passwort für: {userToChangePassword?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-pw">Neues Passwort *</Label>
              <div className="relative">
                <Input
                  id="new-pw"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Mindestens 6 Zeichen"
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-pw">Passwort bestätigen *</Label>
              <Input
                id="confirm-pw"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Passwort wiederholen"
                minLength={6}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setPasswordDialogOpen(false);
                setUserToChangePassword(null);
                setNewPassword('');
                setConfirmPassword('');
              }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isLoading} className="metaxon-primary text-white">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert...</> : 'Passwort ändern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
